import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const DateScrollPicker = ({
  selectedDate,
  onDateSelect,
  userLocation,
  currentSort = 'newest',
  onSortChange,
  onFilterClick
}) => {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // 直近2ヶ月分の日付を生成
  const generateDates = () => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  const dates = generateDates();

  // 日付フォーマット関数
  const formatDate = (date) => {
    // ローカルタイムで YYYY-MM-DD 形式を生成
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}/${day}`;
  };

  const formatDayOfWeek = (date) => {
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    return days[date.getDay()];
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return formatDate(date) === selectedDate;
  };

  // スクロール状態の更新
  const updateScrollState = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    updateScrollState();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollState);
      return () => container.removeEventListener('scroll', updateScrollState);
    }
  }, []);

  // スクロール制御
  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = direction === 'left'
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-white border-b border-gray-200">
      {/* 日付スクロールエリア */}
      <div className="relative">
        {/* 左スクロールボタン */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-0 z-10 h-full w-8 bg-gradient-to-r from-white to-transparent flex items-center justify-start pl-1"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-400" />
          </button>
        )}

        {/* 右スクロールボタン */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-0 z-10 h-full w-8 bg-gradient-to-l from-white to-transparent flex items-center justify-end pr-1"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
          </button>
        )}

        {/* 日付リスト */}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide px-4 py-3 space-x-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {dates.map((date, index) => {
            const dateStr = formatDate(date);
            const isDateSelected = isSelected(date);
            const isDateToday = isToday(date);

            return (
              <button
                key={index}
                onClick={() => onDateSelect(dateStr)}
                className={`flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-lg border transition-all duration-200 ${isDateSelected
                  ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                  : isDateToday
                    ? 'bg-blue-50 text-blue-600 border-blue-200 shadow-sm'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
              >
                <span className={`text-xs font-medium ${isDateSelected ? 'text-white' : 'text-gray-500'}`}>
                  {formatDayOfWeek(date)}
                </span>
                <span className={`text-sm font-bold ${isDateSelected ? 'text-white' : isDateToday ? 'text-blue-600' : 'text-gray-900'}`}>
                  {formatDisplayDate(date)}
                </span>
                {isDateToday && (
                  <div className={`w-1 h-1 rounded-full mt-0.5 ${isDateSelected ? 'bg-white' : 'bg-blue-500'}`} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* フィルター・ソートエリア */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm">
            <button
              onClick={() => onSortChange && onSortChange('distance')}
              className={`font-medium ${currentSort === 'distance' ? 'text-blue-600' : 'text-gray-500'}`}
            >
              現在地から近い順
            </button>
            <button
              onClick={() => onSortChange && onSortChange('newest')}
              className={`font-medium ${currentSort === 'newest' ? 'text-blue-600' : 'text-gray-500'}`}
            >
              新着順
            </button>
            <button
              onClick={() => onSortChange && onSortChange('rating')} // Assuming rating sort exists or will exist
              className={`font-medium ${currentSort === 'rating' ? 'text-blue-600' : 'text-gray-500'}`}
            >
              評価順
            </button>
          </div>
          <button
            onClick={onFilterClick}
            className="text-sm text-gray-500 border border-gray-300 rounded px-3 py-1 hover:bg-gray-50"
          >
            フィルター
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateScrollPicker;